<template>
  <re-input
    ref="inputRef"
    v-model="model"
    class="re-stepper-input"
    type="number"
    v-bind="attrs"
    :model-modifiers="{ ...modelModifiers }"
    @keydown.stop="handleKeydown($event)"
  >
    <template #label-append>
      <slot name="label-append" />
    </template>

    <template #prepend>
      <re-button
        tabindex="-1"
        icon="mdi-minus"
        :size="attrs.size"
        class="btn-stepper btn-stepper--left row"
        :class="{ invalid: inputRef?.hasError }"
        :disable="isDecDisabled"
        flat
        :is-multi-line="false"
        @click="decrement"
      />
    </template>

    <template #embed>
      <re-button
        tabindex="-1"
        icon="mdi-plus"
        :size="attrs.size"
        class="btn-stepper btn-stepper--right row"
        :class="{ invalid: inputRef?.hasError }"
        :disable="isIncDisabled"
        flat
        :is-multi-line="false"
        @click="increment"
      />
    </template>
  </re-input>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue'

import { useReadOnly } from '@/composables/useReadOnly'

const props = defineProps({
  steps: {
    type: Number,
    default: 1,
  },
})

const { isReadOnly } = useReadOnly()

const [model, modelModifiers] = defineModel<number>()
const attrs = useAttrs()

const inputRef = ref<InstanceType<typeof import('./ReInput.vue').default> | null>(null)

function getNumericValue() {
  const val = Number(model.value)
  return isNaN(val) ? 0 : val
}

function increment() {
  const value = getNumericValue() + props.steps
  model.value = value
}

function decrement() {
  const value = getNumericValue() - props.steps
  model.value = value
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'ArrowDown' && !isDecDisabled.value) {
    decrement()
  }
  if (event.key === 'ArrowUp' && !isIncDisabled.value) {
    increment()
  }
}

const isDecDisabled = computed(() => {
  const currentValue = getNumericValue()

  return (
    attrs.readonly
    || attrs.disable
    || isReadOnly.value
    || (attrs.min !== undefined && attrs.min !== null && currentValue <= Number(attrs.min))
  )
})

const isIncDisabled = computed(() => {
  const currentValue = getNumericValue()

  return (
    attrs.readonly
    || attrs.disable
    || isReadOnly.value
    || (attrs.max !== undefined && attrs.max !== null && currentValue >= Number(attrs.max))
  )
})
</script>

<style scoped lang="scss">
.re-stepper-input {
  :deep(.mdi-alert-circle) {
    padding-right: 5px;
  }
  :deep(.q-field__control) {
    padding: 0 !important;
  }

  :deep(.q-btn) {
    width: 2.858em;
  }

  :deep(.q-field__native) {
    text-align: center;
  }
}

.btn-stepper {
  &.invalid {
    :deep(.q-btn) {
      background-color: var(--negative-bground);
      border-color: var(--q-negative);
      .q-btn__content {
        color: var(--q-negative);
      }
    }
  }
  :deep(.q-btn) {
    border: solid 1px var(--q-primary);
    background-color: var(--primary-bground);
    .q-btn__content {
      color: var(--q-primary);
    }
  }
  :deep(.disabled) {
    border-color: unset;
    .q-btn__content {
      color: unset;
    }
  }
  &--left :deep(.q-btn) {
    border-radius: $generic-border-radius 0 0 $generic-border-radius;
  }
  &--right :deep(.q-btn) {
    border-radius: 0 $generic-border-radius $generic-border-radius 0;
  }
}
</style>
